'use client';

import './tax-receipts-list.scss';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.js';
import { ActionCard } from '@sola/ark-core/molecules/action-card/action-card.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { ItemCardSkeleton } from '@sola/ark-core/molecules/item-card/item-card.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { TaxReceipt } from '@sola/schema/dotcom/proxy-api/taxReceipts.js';
import { useTaxReceipts } from '@sola/supporter-sdk/hooks/tax-receipts.js';

import { TaxReceiptCard } from '../../molecules/tax-receipt-card/tax-receipt-card';

/**
 * Get component data
 */
export const useComponentData = () => {
  const { data, isLoading, isFetching, isPending, isError } = useTaxReceipts();
  const taxReceiptsResult: TaxReceipt[] | undefined = data;

  const taxReceipts = useMemo(() => {
    return (
      taxReceiptsResult
        ?.filter((taxReceipt) => taxReceipt.url && taxReceipt.year)
        .sort((a, b) => b.year! - a.year!) || []
    );
  }, [taxReceiptsResult]);
  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;
  const hasData = !!taxReceipts?.length;
  const hasNoReceipts = !isShowingSkeletons && !hasData && !isError;
  const hasReceipts = !isShowingSkeletons && hasData;
  const hasErrorAlert = !isShowingSkeletons && isError;
  const hasFootnote = !isShowingSkeletons && !isError;
  return {
    isShowingSkeletons,
    hasNoReceipts,
    hasReceipts,
    hasErrorAlert,
    hasFootnote,
    taxReceipts,
  };
};

/**
 * Displays list of commitments based on frequency.
 */
export const TaxReceiptsList = () => {
  const { t } = useTranslation('my');

  const {
    isShowingSkeletons,
    hasErrorAlert,
    hasFootnote,
    hasNoReceipts,
    hasReceipts,
    taxReceipts,
  } = useComponentData();

  return (
    <div className="max-w-md flex flex-col gap-comfy-xl">
      {isShowingSkeletons && (
        <>
          <ItemCardSkeleton />
          <ItemCardSkeleton />
          <ItemCardSkeleton />
        </>
      )}

      {hasReceipts && (
        <div className="flex flex-col gap-comfy-xs">
          {taxReceipts!.map((taxReceipt) => (
            <TaxReceiptCard
              key={`tax-receipt-${taxReceipt.year}`}
              taxReceipt={taxReceipt}
            />
          ))}
        </div>
      )}

      {hasNoReceipts && <Alert message={t('taxReceipts.noTaxReceipts')} />}

      {hasErrorAlert && (
        <Alert
          title={t('taxReceipts.requestError.title')}
          message={t('taxReceipts.requestError.message')}
          variant="negative"
          action={
            <Link href={t('ctas.contactSupport.url')}>
              {t('ctas.contactSupport.text')}
            </Link>
          }
        />
      )}

      {hasFootnote && (
        <>
          <p className="label-moderate label-md body-neutral-mild">
            {t('taxReceipts.footnote')}
          </p>

          <ActionCard
            icon={<ArkIcon size="18" iconName="message-question-square" />}
            title={t('taxReceipts.support.title')}
            subtitle={t('taxReceipts.support.subtitle')}
            href={t('ctas.contactSupport.url')}
          />
        </>
      )}
    </div>
  );
};
